import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  SimpleChanges,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import * as go from 'gojs';

import { Answer } from 'src/app/core/api/models';
import { DiagramComponent } from 'src/app/shared/components/diagram/diagram.component';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'base-parser-diagram',
  templateUrl: './../../../../shared/components/diagram/diagram.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatTooltipModule, TranslateModule],
})
export class BaseParserDiagramComponent extends DiagramComponent {
  //#region properties

  @Input() formulas: Array<Answer> = [];
  @Input() diagramId: string = 'diagram';

  //#endregion

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formulas']) this.cdr.detectChanges(); // Force change detection

    this.setDataToDiagram();
  }

  //#region Node config
  configNodes() {
    // Used to convert the node's tree level into a theme color

    this.diagram.nodeTemplate = new go.Node(go.Panel.Spot, {
      selectionObjectName: 'BODY',
    })
      .add(
        new go.Panel(go.Panel.Auto, { name: 'BODY' }).add(
          // define the node's outer shape
          new go.Shape('RoundedRectangle', {
            name: 'SHAPE',
            strokeWidth: 0,
            portId: '',
            spot1: go.Spot.TopLeft,
            spot2: go.Spot.BottomRight,
          }).theme('fill', 'background'),
          new go.Panel(go.Panel.Table, {
            margin: 0.5,
            defaultRowSeparatorStrokeWidth: 0.5,
          })
            .theme('defaultRowSeparatorStroke', 'divider')
            .add(
              new go.Panel(go.Panel.Table, {
                padding: new go.Margin(18, 18, 18, 24),
              })
                .addColumnDefinition(0, { width: 240 })
                .add(
                  new go.Panel(go.Panel.Table, {
                    column: 0,
                    alignment: go.Spot.Right,
                    stretch: go.Stretch.Vertical,
                    defaultAlignment: go.Spot.Right,
                  }).add(
                    new go.Panel(go.Panel.Horizontal, { row: 0 }).add(
                      new go.Panel(go.Panel.Auto, {
                        width: 200,
                        margin: 5,
                      }).add(
                        new go.Panel(go.Panel.Auto).add(
                          new go.TextBlock({
                            editable: false,
                            font: 'bold 12px Vazirmatn',
                          })
                            .bind('text', '', () =>
                              this.translate.instant('common.title')
                            )
                            .theme('stroke', 'text')
                        ),
                        new go.TextBlock(
                          this.translate.instant('common.title'),
                          {
                            editable: false,
                          }
                        )
                          .bindTwoWay('text', 'title', (type) => type || '-')
                          .theme('stroke', 'text')
                      )
                    ),

                    new go.Panel(go.Panel.Horizontal, { row: 1 }).add(
                      new go.Panel(go.Panel.Auto, {
                        width: 230,
                        margin: 5,
                      }).add(
                        new go.Panel(go.Panel.Auto).add(
                          new go.TextBlock({
                            editable: false,
                            font: 'bold 12px Vazirmatn',
                            margin: new go.Margin(3, 0, 0, 0),
                          })
                            .bind('text', '', () =>
                              this.translate.instant('common.keyword')
                            )
                            .theme('stroke', 'text')
                        ),
                        new go.TextBlock({
                          minSize: new go.Size(10, 12),
                          margin: new go.Margin(2, -1),
                          maxSize: new go.Size(150, 150),
                          font: 'bold 12px Vazirmatn',
                        })
                          .theme('stroke', 'badgeText')
                          .bindTwoWay('text', 'answerValue')
                      )
                    ),
                    new go.Panel(go.Panel.Horizontal, { row: 2 }).add(
                      new go.Panel(go.Panel.Auto, {
                        width: 200,
                        margin: 5,
                      }).add(
                        new go.Panel(go.Panel.Auto).add(
                          new go.TextBlock({
                            editable: false,
                            font: 'bold 12px Vazirmatn',
                          })
                            .bind('text', '', () =>
                              this.translate.instant('common.phonebook')
                            )
                            .theme('stroke', 'text')
                        ),
                        new go.TextBlock({
                          editable: false,
                        })
                          .bindTwoWay('text', 'phonebookName')
                          .theme('stroke', 'text')
                      )
                    )
                  )
                ),
              new go.Panel(go.Panel.Table, {
                row: 1,
                defaultColumnSeparatorStrokeWidth: 0.5,
              })
                .theme('defaultColumnSeparatorStroke', 'divider')
                .add(
                  this.addAndRemoveNodesBtn('Add'),
                  this.addAndRemoveNodesBtn('Delete')
                )
            )
        ), // end Auto Panel
        new go.Shape('RoundedLeftRectangle', {
          alignment: go.Spot.Left,
          alignmentFocus: go.Spot.Left,
          stretch: go.Stretch.Vertical,
          width: 6,
          strokeWidth: 0,
        }).themeObject('fill', '', 'levels', this.findLevelColor),
        go.GraphObject.build('TreeExpanderButton', {
          _treeExpandedFigure: 'LineUp',
          _treeCollapsedFigure: 'LineDown',
          name: 'BUTTONX',
          alignment: go.Spot.Bottom,
          opacity: 0, // initially not visible
        })
          // button is visible either when node is selected or on mouse-over
          .bindObject('opacity', 'isSelected', (s) => (s ? 1 : 0))
      )
      // for sorting, have the Node.text be the data.name
      .bind('text', 'answerValue')
      // bind the Part.layerName to control the Node's layer depending on whether it isSelected
      .bindObject('layerName', 'isSelected', (sel) => (sel ? 'Foreground' : ''))
      .bindTwoWay('isTreeExpanded');
  }

  addNewNode(node: go.Node | null = null, includeParent: boolean = true): void {
    if (!node?.data && includeParent) return; // جلوگیری از خطا در صورت مقدار نداشتن node
    let parentNodeData: NodeData | undefined;
    let newNodeData: NodeData;

    const newNodeId = uuidv4();

    this.diagram.model.commit((model) => {
      if (includeParent) {
        parentNodeData = node.data as NodeData;
        newNodeData = {
          title: '-',
          answerValue: '-',
          phonebookName: '-',
          parent: parentNodeData.key,
          key: newNodeId,
        };
      } else {
        newNodeData = {
          title: '-',
          answerValue: '-',
          phonebookName: '-',
          key: newNodeId,
        };
      }

      model.addNodeData(newNodeData);
      const newDiagramNode = this.diagram.findNodeForData(newNodeData);

      if (newDiagramNode) {
        if (includeParent) newDiagramNode.location = node.location;
        else newDiagramNode.location = new go.Point(0, 0);

        this.diagram.commandHandler.scrollToPart(newDiagramNode);
        this.diagram.select(newDiagramNode);

        // انتشار رویدادها
        this.selectedNode.emit(newNodeId);
        this.newNode.emit({
          id: newNodeId,
          parentId: includeParent ? parentNodeData?.key : null,
        });
      }
    }, 'add node');
  }

  addAndRemoveNodesBtn(name: string) {
    // Check if the button is 'Add' or 'Remove'
    const isAdd = name === 'Add';

    return new go.Panel(go.Panel.Table, {
      // Handle mouse hover effects
      mouseEnter: (e, obj: any) =>
        (obj.background = this.currentTheme == 'light' ? '#eaecf9' : '#11183e'),
      mouseLeave: (e, obj: any) => (obj.background = 'transparent'),
      name,
      background: 'transparent',
      cursor: 'Pointer',
      column: isAdd ? 0 : 1,
      width: 170,
      height: 40,
    })
      .add(
        new go.Panel(go.Panel.Horizontal).add(
          new go.Picture({
            source: isAdd
              ? '/assets/media/icons/duotune/general/add-svgrepo-com.svg'
              : '/assets/media/icons/duotune/general/trash-bin-trash-svgrepo-com.svg',
            desiredSize: new go.Size(25, 25),
          })
            .bind('filter', '', () => {
              if (this.currentTheme == 'light') return 'none';
              else return 'invert(1) brightness(2)';
            })
            .theme('fill', 'text')
            .themeData('fill', name, null),
          new go.TextBlock(
            isAdd
              ? this.translate.instant('common.add')
              : this.translate.instant('common.delete')
          )
            .theme('stroke', 'text')
            .themeData('stroke', name, null)
            .theme('font', 'link')
        )
      )
      .set({
        cursor: 'pointer',
        click: (e, obj: any) => {
          // Add or remove node based on button type
          if (isAdd) this.addNewNode(obj.part);
          else this.removeSelectedNode(obj.part);
        },
      });
  }

  //#endregion

  setDataToDiagram() {
    let formulas = this.formulas.map((item) => {
      const transformedItem: any = {
        key: item.id,
        title: item?.title || '-',
        answerValue: item.answerValue || '-',
        phonebookName: item.phonebookName || '-',
      };

      if (item.parentId !== null && item.parentId !== undefined) {
        transformedItem.parent = item.parentId;
      }

      return transformedItem;
    });

    this.diagram.model = new go.TreeModel(formulas);
  }
}

interface NodeData {
  title?: string;
  answerValue?: string;
  phonebookName?: any;
  key?: string;
  parent?: string;
}
